"use client";

import React, { useEffect, useState } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  Paper,
  TablePagination,
  TextField,
  Typography,
  CircularProgress,
  Card,
  CardHeader,
  CardContent,
  InputAdornment,
  Chip,
  IconButton,
  Tooltip,
  alpha,
  useTheme,
  Button
} from "@mui/material";
import {
  Search,
  Refresh,
  FilterList,
  DateRange,
} from "@mui/icons-material";

const Utmrecords = () => {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const theme = useTheme();

  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);

  // Filter state
  const [filter, setFilter] = useState("");

  const fetchData = async () => {
    try {
      setLoading(true);
      const res = await fetch(
        `/api/v1/utm/getall-utm?page=${page + 1}&limit=${rowsPerPage}&utm_source=${filter}`
      );
      const data = await res.json();

      if (data.success) {
        setRecords(data.data);
        setTotalRecords(data.pagination.total);
      }
    } catch (error) {
      console.error("Error fetching UTM records:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [page, rowsPerPage, filter]);

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleRefresh = () => {
    fetchData();
  };

  const getStatusColor = (status) => {
    return theme.palette.mode === 'dark' ? theme.palette.primary.light : theme.palette.primary.main;
  };

  return (
    <Box sx={{ p: 3, maxWidth: '1400px', margin: '0 auto' }}>

      <CardHeader
        title={
          <Typography
            variant="h4"
            component="h1"
            fontWeight={600}
            color="primary"
            sx={{ textAlign: "left" }} // makes sure it's left aligned
          >
            UTM Records
          </Typography>
        }


        action={
          <Tooltip title="Refresh data">
            <IconButton
              onClick={handleRefresh}
              sx={{
                backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.1),
                "&:hover": {
                  backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.2),
                },
              }}
            >
              <Refresh />
            </IconButton>
          </Tooltip>
        }
      />



      {/* Filter Section */}
      <Card elevation={1} sx={{ mb: 3 }}>
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <TextField
              label="Filter by UTM Source"
              variant="outlined"
              size="small"
              value={filter}
              onChange={(e) => {
                setPage(0);
                setFilter(e.target.value);
              }}
              sx={{
                minWidth: 300,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search color="action" />
                  </InputAdornment>
                ),
              }}
              placeholder="Search UTM sources..."
            />

            <Chip
              label={`${totalRecords} Total Records`}
              variant="outlined"
              color="primary"
            />
          </Box>
        </CardContent>
      </Card>

      {/* Table Section */}
      <Card elevation={1}>
        <TableContainer
          component={Paper}
          elevation={0}
          sx={{
            border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
            borderRadius: 2,
          }}
        >
          {loading ? (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              minHeight="200px"
            >
              <Box textAlign="center">
                <CircularProgress size={40} thickness={4} />
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                  Loading UTM records...
                </Typography>
              </Box>
            </Box>
          ) : (
            <>
              <Table sx={{ minWidth: 650 }}>
                <TableHead>
                  <TableRow sx={{
                    backgroundColor: alpha(theme.palette.primary.main, 0.02),
                    '& th': {
                      fontWeight: '600',
                      fontSize: '0.875rem',
                      borderBottom: `2px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                    }
                  }}>
                    <TableCell sx={{ borderRadius: '8px 0 0 0' }}>ID</TableCell>
                    <TableCell>Table Name</TableCell>
                    <TableCell>Row ID</TableCell>
                    <TableCell>UTM Source</TableCell>
                    <TableCell>UTM Details</TableCell>
                    <TableCell sx={{ borderRadius: '0 8px 0 0' }}>Created At</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {records.length > 0 ? (
                    records.map((row, index) => (
                      <TableRow
                        key={row.id}
                        sx={{
                          '&:last-child td': { borderBottom: 0 },
                          backgroundColor: index % 2 === 0 ?
                            alpha(theme.palette.action.hover, 0.02) : 'transparent',
                          transition: 'all 0.2s ease-in-out',
                          '&:hover': {
                            backgroundColor: alpha(theme.palette.primary.main, 0.04),
                            transform: 'translateY(-1px)',
                            boxShadow: theme.shadows[1],
                          }
                        }}
                      >
                        <TableCell>
                          <Chip
                            label={row.id}
                            size="small"
                            variant="outlined"
                            color="primary"
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={row.table_name}
                            size="small"
                            sx={{
                              backgroundColor: getStatusColor(),
                              color: 'white',
                              fontWeight: '500',
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontFamily="monospace">
                            {row.table_row_id}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography
                            variant="body2"
                            sx={{
                              maxWidth: 200,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                            title={row.utm_source}
                          >
                            {row.utm_source}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {(() => {
                            let utmData = row.raw_utm;
                            if (typeof utmData === 'string') {
                              try {
                                utmData = JSON.parse(utmData);
                              } catch (e) {
                                console.error("Error parsing raw_utm:", e);
                                utmData = null;
                              }
                            }

                            if (utmData && typeof utmData === 'object') {
                              return (
                                <Box>
                                  {Object.entries(utmData)
                                    .filter(([_, v]) => v && v !== "" && v !== null && v !== undefined)
                                    .map(([k, v]) => (
                                      <Typography key={k} variant="body2" sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                                        <strong>{k.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}:</strong> {v}
                                      </Typography>
                                    ))}
                                </Box>
                              );
                            }

                            return (
                              <Typography
                                variant="body2"
                                sx={{
                                  maxWidth: 300,
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                }}
                                title={row.utm_details}
                              >
                                {row.utm_details}
                              </Typography>
                            );
                          })()}
                        </TableCell>
                        <TableCell>
                          <Box>
                            <Typography variant="body2" fontWeight="500">
                              {new Date(row.createdAt).toLocaleDateString()}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {new Date(row.createdAt).toLocaleTimeString()}
                            </Typography>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                        <Box sx={{ textAlign: 'center' }}>
                          <Search sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
                          <Typography variant="h6" color="text.secondary" gutterBottom>
                            No records found
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {filter ? `No results for "${filter}"` : 'Try adjusting your search filter'}
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </>
          )}
        </TableContainer>

        {/* Pagination */}
        <TablePagination
          component="div"
          count={totalRecords}
          page={page}
          onPageChange={handlePageChange}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleRowsPerPageChange}
          rowsPerPageOptions={[5, 10, 25, 50]}
          sx={{
            borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
            '& .MuiTablePagination-toolbar': {
              padding: 2,
            }
          }}
        />
      </Card>
    </Box>
  );
};

export default Utmrecords;